import { decodeHTML } from 'entities';
import type { parsePath } from '@/utils/parse-path';
import type { ApiResponse } from '@/utils/data/types';
import type { CustomPostEntity } from '@/types/blocks';
import type { BlockContext } from '@headstartwp/core/react';
import type { PostLookup, ExtendedPostLookup } from '@/types/entities';

export function decodeEntities(text: string | undefined) {
	if (!text) {
		return '';
	}

	return decodeHTML(text);
}

export function getPostLookupParams(
	obj: PostLookup,
	requestedPage?: null | number,
): PostLookup {
	const pagination = obj.pagination || false;
	const page = pagination ? requestedPage || 1 : 1;

	return {
		numberOfPosts: obj.numberOfTerms || obj.numberOfPosts,
		page,
		pagination,
		postType: obj.postType,
		taxonomy: obj.taxonomy,
		terms: obj.terms,
	};
}

export function serializePostLookup(
	lookup: PostLookup,
	requestedPage?: null | number,
): string {
	return JSON.stringify(getPostLookupParams(lookup, requestedPage));
}

export function getExtendedLookupParameters(
	lookup: ExtendedPostLookup,
	requestedPage?: null | number,
): ExtendedPostLookup {
	let { curationType, posts, terms } = lookup;

	if (!('posts' in lookup)) {
		posts = terms as unknown as CustomPostEntity[];
		terms = [];

		if (lookup.curationType === 'automatic') {
			// This is a temporary fix for the automatic curation type for taxonomy
			curationType = 'manual';
		}
	}

	return {
		...getPostLookupParams(lookup, requestedPage),
		curationType,
		posts,
		terms,
		title: decodeEntities(lookup.title),
	};
}

export function serializePostLookupKey(
	lookup: ExtendedPostLookup,
	requestedPage?: null | number,
): string {
	const { posts, ...rest } = getExtendedLookupParameters(lookup, requestedPage);

	// Use a combination of post ids to create a unique key to avoid duplicate blocks
	const postIds = posts?.map((post) => post.id);

	return JSON.stringify({
		...rest,
		postIds,
	});
}

export function getSerializedPosts(
	blockAttributes: unknown,
	blockContext: BlockContext | undefined,
): {
	posts: CustomPostEntity[];
	totalPages: number;
} {
	let posts: CustomPostEntity[] = [];
	let totalPages = 0;
	const parsedPath = blockContext?.parsedPath as ReturnType<typeof parsePath>;
	const uniqueBlocks = (blockContext?.uniqueBlocks || {}) as Record<
		string,
		ApiResponse<CustomPostEntity[]>
	>;
	const key = serializePostLookupKey(
		blockAttributes as unknown as ExtendedPostLookup,
		parsedPath.page,
	);

	if (blockContext && key in uniqueBlocks) {
		const data = uniqueBlocks[key];
		totalPages = Number(data.headers['x-wp-totalpages']);
		posts = data.json;
	}

	return {
		posts,
		totalPages,
	};
}
